import React, { useEffect } from "react";

import type * as UDM from "@busbud/int-schemas/udm";
import type { Vehicle } from "@busbud/int-schemas/udm";

import { useAppContext } from "@app/helpers/hooks";
import { getSeatById } from "@app/helpers/udm/vehicle";
import { Money } from "@app/lib/money";
import { getAllSelectedSeatsFromCart } from "@app/modules/async-cart/selectors/get-all-selected-seats-from-cart";
import { getAsyncCartSegmentsVehicleLayout } from "@app/modules/async-cart/selectors/get-async-cart-segments-vehicle-layouts";
import { useResultsSelector } from "@app/modules/results/hooks/use-results-selector";
import type { CurrencyCode } from "@app/types/currency";
import type { NapiV2SelectedSeat } from "@app/types/napi-v2-passengers-types";
import type { PassengerSeatSelectionInstance } from "@app/types/seat-selection-types";

export interface SeatPrices {
  subtotal: number;
  categories: {
    [passenger_category: string]: number;
  };
}

interface Props {
  currency: CurrencyCode;
  prices: SeatPrices;
  passengers: PassengerSeatSelectionInstance[];
  selected_seats: UDM.SeatCell[];
  segment_id: string;
}

const getSeatAdditionalPrice = (
  prices: UDM.SeatCellPassengerPrice[],
  passenger_category: PassengerSeatSelectionInstance["passenger_category"]
): number => {
  const seat_price = prices.find(
    price => price.category === passenger_category
  );
  return seat_price ? seat_price.base : 0;
};

const getBasePrice = (
  prices: SeatPrices,
  passenger_category: PassengerSeatSelectionInstance["passenger_category"]
): number => {
  // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  return prices.categories[passenger_category];
};

export const SeatPrice: React.FC<Props> = props => {
  const {
    prices,
    passengers,
    currency,
    selected_seats: picked_seats,
    segment_id
  } = props;
  const { locale } = useAppContext();
  const cart_vehicles_layout = useResultsSelector(
    getAsyncCartSegmentsVehicleLayout
  );
  const cart_saved_seats = useResultsSelector(getAllSelectedSeatsFromCart);

  // We save a reference to the cart_saved_seats in the internal state to avoid re-rendering and triggering the `isSamePickedSeat`rule on a small timeframe between the cart is updated and the modal is closed.
  const [cached_cart_saved_seats, setCartSavedSeats] = React.useState<
    NapiV2SelectedSeat[]
  >([]);
  useEffect(() => {
    setCartSavedSeats(cart_saved_seats);
  }, []);

  const current_vehicle_layout = cart_vehicles_layout[
    segment_id
  ] as unknown as UDM.Vehicle["layout"];

  const total_price = passengers.reduce((total_cost, passenger) => {
    const seat_base_price = getBasePrice(prices, passenger.passenger_category);

    const passenger_selected_seat = passenger.selected_seats.find(
      seat => seat.segment_id === segment_id
    );

    // if passenger doesn't have selected seat, we add the base price
    if (!passenger_selected_seat) {
      return total_cost + seat_base_price;
    }

    // get the current selected_seat
    const current_picked_seat = picked_seats.find(
      ({ id }) => id === passenger_selected_seat.seat_id
    );

    // check if the selected seat is already in the cart
    const cart_saved_seat = cached_cart_saved_seats.find(
      seat => seat.segment_id === segment_id
    );

    // if the current selected_seat in the cart is the same as the one in internal state just return the seat_base_price, as the additional price is already added to the base price
    if (
      isSamePickedSeat(cart_saved_seat, current_picked_seat) ||
      !current_picked_seat ||
      !current_picked_seat.prices
    ) {
      return total_cost + seat_base_price;
    }

    const cart_selected_seat_additional_price = getSavedSeatAdditionalPrice(
      current_vehicle_layout,
      cart_saved_seat,
      passenger
    );

    const seat_additional_price = getSeatAdditionalPrice(
      current_picked_seat.prices,
      passenger.passenger_category
    );

    return sumTotalPrice(
      total_cost,
      seat_base_price,
      cart_selected_seat_additional_price,
      seat_additional_price
    );
  }, 0);

  return <>{new Money(total_price).getPrice(currency, locale)}</>;
};

const isSamePickedSeat = (
  cart_saved_seat: NapiV2SelectedSeat | undefined,
  current_picked_seat: UDM.SeatCell | undefined
) => {
  return (
    cart_saved_seat &&
    current_picked_seat &&
    cart_saved_seat?.seat_id === current_picked_seat?.id
  );
};

const sumTotalPrice = (
  acc: number,
  seat_base_price: number,
  saved_seat_additional_price: number,
  picked_seat_additional_price: number
) => {
  return (
    acc +
    seat_base_price +
    (picked_seat_additional_price - saved_seat_additional_price)
  );
};

const getSavedSeatAdditionalPrice = (
  current_vehicle_layout: Vehicle["layout"],
  cart_saved_seat: NapiV2SelectedSeat | undefined,
  passenger: PassengerSeatSelectionInstance
) => {
  const cart_selected_seat_additional_prices =
    current_vehicle_layout && cart_saved_seat
      ? getSeatById(
          { layout: current_vehicle_layout } as unknown as Vehicle,
          cart_saved_seat.seat_id
        )?.prices
      : undefined;

  const cart_selected_seat_additional_price = getSeatAdditionalPrice(
    cart_selected_seat_additional_prices || [],
    passenger.passenger_category
  );
  return cart_selected_seat_additional_price;
};
